import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Download, MessageCircle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { setLeadSource } from '@/lib/leadSource';

/**
 * ClosingCTA — premium closing block for the bottom of every standalone page.
 *
 * Visual: warm orange/teal gradient, big benefit-led title, 1 primary CTA
 * (demo) + 2 quiet links (brochure, whatsapp).
 *
 * Props:
 *  - source : lead-source attribution prefix (e.g. "about-closing")
 */
export const ClosingCTA = ({ source = 'page-closing' }) => {
  const { locale } = useLanguage();

  const c = locale === 'en'
    ? {
        eyebrow: '30 minutes · no commitment',
        title_a: 'See it on your real workflow,',
        title_b: 'before you change anything.',
        body:
          'In a guided 30-min demo we look at your practice, your bottlenecks and your data. We tell you honestly if Prime Vision is the right fit — and where it would save the most time. No pressure, no sales script.',
        primary: 'Book a free demo',
        brochure: 'Download the brochure',
        whatsapp: 'Message us on WhatsApp',
      }
    : {
        eyebrow: '30 minuti · zero impegno',
        title_a: 'Vedila sul tuo flusso reale,',
        title_b: 'prima di cambiare qualunque cosa.',
        body:
          'In una demo guidata di 30 minuti guardiamo il tuo studio, i tuoi colli di bottiglia e i tuoi dati. Ti diciamo con onestà se Prime Vision fa al caso tuo — e dove ti farebbe risparmiare più tempo. Senza pressione commerciale.',
        primary: 'Prenota una demo gratuita',
        brochure: 'Scarica la brochure',
        whatsapp: 'Scrivici su WhatsApp',
      };

  const waMsg = locale === 'en'
    ? 'Hi!%20I%27d%20love%20to%20see%20Prime%20Vision%20on%20my%20workflow.'
    : 'Ciao!%20Vorrei%20vedere%20Prime%20Vision%20sul%20mio%20flusso.';

  return (
    <section
      data-testid={`pv-closing-cta-${source}`}
      className="relative bg-white py-20 md:py-28 border-t border-slate-100"
    >
      <div className="pv-container">
        <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-pv-ice/40 to-white p-8 md:p-14 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.18)]">
          <div className="absolute -top-32 -right-24 w-72 h-72 rounded-full bg-pv-orange/15 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-28 -left-20 w-60 h-60 rounded-full bg-pv-teal/12 blur-3xl pointer-events-none" />

          <div className="relative max-w-2xl">
            <span className="inline-flex items-center gap-2 text-[11px] md:text-[12px] font-bold uppercase tracking-[0.18em] text-pv-orangeDeep font-mono">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-pv-orange" />
              {c.eyebrow}
            </span>

            <h2 className="mt-5 font-heading font-bold tracking-normal text-[30px] sm:text-[38px] md:text-[46px] leading-[1.05] text-pv-ink">
              {c.title_a}
              <br />
              <span className="text-pv-teal">{c.title_b}</span>
            </h2>

            <p className="mt-5 text-slate-600 text-[15px] md:text-[16.5px] leading-[1.65]">
              {c.body}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 sm:gap-6">
              <Link
                to="/bookacall"
                onClick={() => setLeadSource(`${source}-demo`)}
                className="pv-btn-primary group justify-center"
                data-testid={`pv-closing-cta-${source}-demo`}
              >
                {c.primary}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>

              <Link
                to="/brochure"
                onClick={() => setLeadSource(`${source}-brochure`)}
                className="pv-btn-download group justify-center"
                data-testid={`pv-closing-cta-${source}-brochure`}
              >
                <Download className="w-3.5 h-3.5 transition-transform group-hover:-translate-y-0.5" />
                <span>{c.brochure}</span>
              </Link>

              <a
                href={`https://wa.me/393520526856?text=${waMsg}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setLeadSource(`${source}-whatsapp`)}
                className="pv-link-quiet justify-center sm:justify-start"
                data-testid={`pv-closing-cta-${source}-whatsapp`}
              >
                <MessageCircle className="w-3.5 h-3.5" />
                {c.whatsapp}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClosingCTA;
